import { useTranslation } from '@kostbar/i18n'
import type { ReactNode } from 'react'

interface CountReviewSectionProps {
  label: string
  /** Rows in this section still awaiting a decision; absent at zero. */
  openCount: number
  /** One line under the label, when the section needs to say what it expects of the counter. */
  hint?: string
  /** A section-wide control, rendered on the heading line ("Restliche N ignorieren"). */
  action?: ReactNode
  children: ReactNode
}

/**
 * One band of the review screen — Fehlmengen, Überschüsse or Nicht erfasst.
 *
 * The three replaced a single "Abweichungen" accordion: a shortage, a surplus and an article that
 * was never captured are three different decisions, and folding them into one collapsible list
 * made the counter scroll past the ones they were not working on. The label is a quiet heading
 * rather than a control, so nothing here can be collapsed away by accident.
 *
 * The open count is grey, not a red badge (CHG-2026-09-inventory-count-review-ux): red on this
 * screen belongs to the shortages themselves, and a red "2" over the surplus section said the
 * wrong thing. The rows below run edge to edge, so the section pulls out of the page padding and
 * each row pays its own.
 */
export function CountReviewSection({
  label,
  openCount,
  hint,
  action,
  children,
}: CountReviewSectionProps) {
  const { t } = useTranslation('inventory-count')
  return (
    <section className="-mx-4 flex flex-col">
      <div className="flex items-center justify-between gap-3 px-4 pb-2.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </h2>
          {openCount > 0 && (
            <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
              {t('review.sectionOpen', { count: openCount })}
            </span>
          )}
        </div>
        {action}
      </div>
      {hint != null && (
        <p className="px-4 pb-2 text-xs text-muted-foreground">{hint}</p>
      )}
      <div className="border-t border-border">{children}</div>
    </section>
  )
}
